import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Navbar from "../components/Navbar/Navbar";
import { apiCall } from "../api/apiCall";
import { User } from "../models/user";
import generateToastContainer from "../utils/ToastContainer";
import "./../home/HomeForm.css";
import TopHeader from "../components/Navbar/TopHeader";
import FeatureGate from "../components/FeatureGate";
import { useTranslation } from "../i18n/UseTranslation";
import StatCardUser from "../home/StatCardUser";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const PAGE_SIZE = 20;
const COLOR_CLASSES = [
  "avatar-teal",
  "avatar-blue",
  "avatar-amber",
  "avatar-purple",
  "avatar-coral",
];

interface CompanyGroup {
  companyId: string;
  companyName: string;
  users: User[];
}

function groupByCompany(users: User[]): CompanyGroup[] {
  const map = new Map<string, CompanyGroup>();
  for (const user of users) {
    const companyId = user.company?.uuid ?? "__no_company__";
    const companyName = user.company?.name ?? "No company";
    if (!map.has(companyId))
      map.set(companyId, { companyId, companyName, users: [] });
    map.get(companyId)!.users.push(user);
  }
  return Array.from(map.values()).sort((a, b) =>
    a.companyName.localeCompare(b.companyName),
  );
}

function initials(firstName: string, lastName: string): string {
  return `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();
}

function companyInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const map: Record<string, { label: string; className: string }> = {
    active: { label: t.home.active, className: "badge badge--active" },
    blocked: { label: t.home.blocked, className: "badge badge--blocked" },
    left_company: { label: t.home.left, className: "badge badge--left" },
  };
  const { label, className } = map[status] ?? {
    label: status,
    className: "badge badge--neutral",
  };
  return <span className={className}>{label}</span>;
}

function ToggleStatusButton({ user }: { user: User }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState(user.status ?? "active");

  const mutation = useMutation({
    mutationFn: (newStatus: "active" | "blocked") =>
      apiCall(`/users/${user.firebaseUid}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      }),
    onSuccess: (_, newStatus) => {
      setStatus(newStatus);
      toast.success(t.admins.statusUpdated);
      queryClient.invalidateQueries({ queryKey: ["fetchAllAdmins"] });
      queryClient.invalidateQueries({ queryKey: ["user", user.firebaseUid] });
    },
    onError: (err: Error) => {
      toast.error(`${t.common.error}: ${err.message}`);
    },
  });

  return (
    <label
      className="home-toggle-switch"
      title={status === "active" ? t.admins.block : t.admins.activate}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={status === "active"}
        disabled={mutation.isPending}
        onChange={(e) => {
          e.stopPropagation();
          mutation.mutate(e.target.checked ? "active" : "blocked");
        }}
      />
      <span className="toggle-slider" />
    </label>
  );
}

interface CompanyTableProps {
  group: CompanyGroup;
  searchQuery: string;
  colorIndex: number;
}

function CompanyTable({ group, searchQuery, colorIndex }: CompanyTableProps) {
  const { t } = useTranslation();
  const translationUser = t.users;
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return group.users;
    return group.users.filter((u) => {
      const rawRole = u.role?.name || "";
      const translatedRole =
        translationUser[rawRole as keyof typeof translationUser] || rawRole;

      return (
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
        (u.email ?? "").toLowerCase().includes(q) ||
        translatedRole.toLowerCase().includes(q) ||
        (u.status ?? "active").toLowerCase().includes(q) ||
        (u.company?.name ?? "").toLowerCase().includes(q) ||
        (u.phone_number ?? "").toLowerCase().includes(q)
      );
    });
  }, [group.users, searchQuery, translationUser]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const sliced = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const avatarColor = COLOR_CLASSES[colorIndex % COLOR_CLASSES.length];

  return (
    <div className="company-block">
      <button
        className="company-header"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
      >
        <div className={`company-avatar ${avatarColor}`}>
          {companyInitials(group.companyName)}
        </div>
        <span className="company-name">{group.companyName}</span>
        <span className={`company-count ${avatarColor}`}>
          {filtered.length}{" "}
          {filtered.length !== 1 ? t.admins.countPlural : t.admins.count}
          {searchQuery && filtered.length !== group.users.length
            ? ` / ${group.users.length}`
            : ""}
        </span>
        <span className={`chevron-icon ${open ? "chevron-open" : ""}`}>
          &#8964;
        </span>
      </button>

      {open && (
        <div className="table-container">
          {filtered.length === 0 ? (
            <p className="empty-state">{t.admins.noMatch}</p>
          ) : (
            <>
              <div className="table-scroll">
                <table className="members-table">
                  <thead>
                    <tr>
                      <th>{t.home.table.name}</th>
                      <th>{t.home.table.email}</th>
                      <th>{t.home.table.role}</th>
                      <th>{t.home.table.phone}</th>
                      <th>{t.home.table.status}</th>
                      <th>{t.home.table.access}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sliced.map((user) => (
                      <tr
                        key={user.firebaseUid}
                        className="member-row"
                        onClick={() => navigate(`/users/${user.firebaseUid}`)}
                      >
                        <td>
                          <div className="user-cell">
                            <div className={`company-avatar ${avatarColor}`}>
                              {initials(user.first_name, user.last_name)}
                            </div>
                            <span>
                              {user.first_name} {user.last_name}
                            </span>
                          </div>
                        </td>
                        <td className="text-secondary">{user.email}</td>
                        <td>
                          <span className="badge badge--neutral">
                            {translationUser[
                              user.role?.name as keyof typeof translationUser
                            ] ||
                              user.role?.name ||
                              "—"}
                          </span>
                        </td>
                        <td className="text-secondary">
                          {user.phone_number ?? "—"}
                        </td>
                        <td>
                          <StatusBadge status={user.status ?? "active"} />
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <ToggleStatusButton user={user} />
                        </td>
                        <td className="arrow-cell">&#8250;</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="pagination">
                  <span className="pagination-info">
                    {t.home.page} {currentPage} / {totalPages}
                    {searchQuery && filtered.length !== group.users.length
                      ? ` — ${filtered.length} ${filtered.length !== 1 ? t.pagination.results : t.pagination.result}`
                      : ""}
                  </span>
                  <div className="pagination-controls">
                    <button
                      className="pg-btn"
                      disabled={currentPage === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      ‹
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(
                        (p) =>
                          p === 1 ||
                          p === totalPages ||
                          Math.abs(p - currentPage) <= 1,
                      )
                      .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                        if (idx > 0 && p - (arr[idx - 1] as number) > 1)
                          acc.push("…");
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, idx) =>
                        p === "…" ? (
                          <span key={`dots-${idx}`} className="pg-dots">
                            …
                          </span>
                        ) : (
                          <button
                            key={p}
                            className={`pg-btn ${p === currentPage ? "pg-btn--active" : ""}`}
                            onClick={() => setPage(p as number)}
                          >
                            {p}
                          </button>
                        ),
                      )}
                    <button
                      className="pg-btn"
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const { data: allUsers } = useQuery<User[], Error>({
    queryKey: ["fetchAllUsers"],
    queryFn: () => apiCall<User[]>("/users", { method: "GET" }),
    enabled: isAuthReady,
  });

  const {
    data,
    isLoading: isLoadingAdmins,
    isError,
    error,
  } = useQuery<User[], Error>({
    queryKey: ["fetchAllAdmins"],
    queryFn: () => apiCall<User[]>("/admins", { method: "GET" }),
    enabled: isAuthReady,
  });

  const { data: currentUser, isLoading: isLoadingMe } = useQuery<User, Error>({
    queryKey: ["fetchCurrentUser"],
    queryFn: async () => apiCall<User>("/users/me", { method: "GET" }),
    enabled: isAuthReady,
  });

  const userToGlimpse = currentUser as any;
  const userSubscription =
    userToGlimpse?.Company?.Subscription ||
    userToGlimpse?.company?.Subscription ||
    userToGlimpse?.company?.subscription;

  const currentPlan =
    userSubscription?.status === "active" ? userSubscription.plan_code : "free";

  const isLoading = isLoadingAdmins || isLoadingMe;

  const displayedAdmins = useMemo(() => {
    return (allUsers ?? []).filter((u) => u.role?.name === "admin");
  }, [allUsers]);

  const groups = useMemo(
    () => groupByCompany(displayedAdmins),
    [displayedAdmins],
  );

  const statsUsers = useMemo(() => {
    const users = allUsers ?? [];
    return {
      total: users.length,
      companies: groups.length,
      active: users.filter((u) => (u.status ?? "active") === "active").length,
      blocked: users.filter((u) => u.status === "blocked").length,
    };
  }, [allUsers, groups]);

  const stats = useMemo(() => {
    const users = data ?? [];
    return {
      total: users.length,
      companies: groups.length,
      active: users.filter((u) => (u.status ?? "active") === "active").length,
      blocked: users.filter((u) => u.status === "blocked").length,
    };
  }, [data, groups]);

  const employeeLimit = userSubscription?.employee_limit;
  const isLimitReached =
    employeeLimit != null && statsUsers.total >= employeeLimit;

  if (isLoading) {
    return (
      <div className="dashboard-wrapper">
        <Navbar />
        <TopHeader />
        <div className="dashboard-content">
          <FeatureGate
            userPlan={currentPlan}
            requiredPlan="essential"
            featureName={t.admins.adminManagement}
          >
            <div className="page-header">
              <h1>{t.admins.title}</h1>
            </div>
            <div className="loading-grid">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="skeleton-block" />
              ))}
            </div>
          </FeatureGate>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="dashboard-wrapper">
        <Navbar />
        <TopHeader />
        <div className="dashboard-content">
          <FeatureGate
            userPlan={currentPlan}
            requiredPlan="essential"
            featureName={t.admins.adminManagement}
          >
            <div className="page-header">
              <h1>{t.admins.title}</h1>
            </div>
            <div className="error-state">
              <span className="error-icon">⚠</span>
              <p>
                {t.admins.failedToLoad} {error.message}
              </p>
            </div>
          </FeatureGate>
        </div>
        {generateToastContainer()}
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Navbar />
      <TopHeader />
      <div className="dashboard-content">
        {/* Header */}
        <FeatureGate
          userPlan={currentPlan}
          requiredPlan="essential"
          featureName={t.admins.adminManagement}
        >
          <div className="page-header">
            <div>
              <h1>{t.admins.title}</h1>
              <p className="page-subtitle">{t.admins.subtitle}</p>
            </div>
            <button
              className="add-policy-submit"
              onClick={() => {
                return !isLimitReached && navigate("/add-admin");
              }}
              disabled={isLimitReached}
              title={isLimitReached ? t.admins.limitExceededEmployee : ""}
              style={
                isLimitReached ? { opacity: 0.5, cursor: "not-allowed" } : {}
              }
            >
              <span className="btn-add-icon">＋</span> {t.admins.addBtn}
            </button>
          </div>

          {/* Stats */}

          <div className="stats-row">
            <StatCardUser
              title={t.admins.total}
              value={stats.total}
              variant="primary"
            />
            <StatCardUser
              title={t.home.companies}
              value={stats.companies}
              variant="primary"
            />
            <StatCardUser
              title={t.home.active}
              value={stats.active}
              variant="active"
            />
            <StatCardUser
              title={t.home.blocked}
              value={stats.blocked}
              variant="blocked"
            />
          </div>

          {/* Search */}
          <div className="toolbar">
            <div className="search-wrapper">
              <span className="search-icon">&#128269;</span>
              <input
                type="text"
                className="search-input"
                placeholder={`    ${t.searchPlaceholderCompany}`}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
              />
              {searchQuery && (
                <button
                  className="search-clear"
                  onClick={() => {
                    setSearchQuery("");
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          {groups.length === 0 ? (
            <div className="empty-state-page">
              <p>{t.admins.notFound}</p>
            </div>
          ) : (
            groups.map((group, idx) => (
              <CompanyTable
                key={group.companyId}
                group={group}
                searchQuery={searchQuery}
                colorIndex={idx}
              />
            ))
          )}
        </FeatureGate>
      </div>
      {generateToastContainer()}
    </div>
  );
}
